import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Upload, Select, Switch, message, Space, Radio } from 'antd';
import { UploadOutlined, FileTextOutlined } from '@ant-design/icons';
import { createPolicy, updatePolicy, getPolicy } from '../../../api/policyApi';
import { getIndustries } from '../../../api/commonApi';

const { TextArea } = Input;
const { Option } = Select;

const PolicyForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [file, setFile] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);

  useEffect(() => {
    const loadIndustries = async () => {
      setIndustriesLoading(true);
      try {
        const data = await getIndustries();
        setIndustries(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading industries:', error);
      } finally {
        setIndustriesLoading(false);
      }
    };
    loadIndustries();
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      const loadPolicy = async () => {
        try {
          const response = await getPolicy(id);
          const data = response.data?.data;
          if (data) {
            const releaseDate = data.releaseDate && !isNaN(new Date(data.releaseDate))
              ? new Date(data.releaseDate).toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0];

            form.setFieldsValue({
              title: data.title,
              description: data.description,
              type: data.type,
              sector: data.sector,
              isInternational: data.isInternational ?? false,
              releaseDate,
            });
            
            // If file exists in database (as GridFS), show preview
            if (data.fileId || data.fileName) {
              setFilePreview(data.fileName || 'Existing file');
            }
          }
        } catch (error) {
          console.error('Error loading policy:', error);
          message.error('Failed to load policy data');
        }
      };
      loadPolicy();
    } else {
      // Set default values for new policy
      form.setFieldsValue({
        type: 'policy',
        isInternational: false,
        releaseDate: new Date().toISOString().split('T')[0],
      });
    }
  }, [isEdit, id, form]);

  const handleFileChange = (info) => {
    const selectedFile = info.file.originFileObj || info.file;
    
    if (selectedFile) {
      // Validate file type
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      const allowedTypes = ['pdf', 'docx', 'doc', 'txt'];
      
      if (!allowedTypes.includes(fileExt)) {
        message.error('Invalid file type. Allowed types: PDF, DOCX, DOC, TXT');
        return;
      }

      // Validate file size (50MB)
      const isLt50M = selectedFile.size / 1024 / 1024 < 50;
      if (!isLt50M) {
        message.error('File must be smaller than 50MB!');
        return;
      }

      setFile(selectedFile);
      setFilePreview(selectedFile.name);
    }
  };

  const handleSubmit = async (values) => {
    console.log("Form data:", values);

    // For new policies, file is required
    if (!isEdit && !file) {
      message.error('Please upload a file');
      return;
    }

    setIsLoading(true);

    try {
      // Convert file to base64
      let fileData = null;
      let fileName = null;
      let fileType = null;
      let fileSize = null;

      if (file) {
        const reader = new FileReader();
        fileData = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        fileName = file.name;
        fileType = file.name.split('.').pop().toLowerCase();
        fileSize = file.size;
        console.log('File converted to base64, size:', fileSize);
      }

      // Prepare JSON payload
      const payload = {
        ...values,
        fileData,
        fileName,
        fileType,
        fileSize
      };

      console.log('Sending payload with file data');

      let response;
      if (isEdit && id) {
        response = await updatePolicy(id, payload);
        message.success(`${values.type === 'policy' ? 'Policy' : 'Scheme'} updated successfully`);
      } else {
        response = await createPolicy(payload);
        message.success(`${values.type === 'policy' ? 'Policy' : 'Scheme'} created successfully`);
      }

      navigate('/admin/policies');
    } catch (error) {
      console.error('Error saving policy:', error);
      message.error(error.response?.data?.message || error.message || `Failed to save ${values.type}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f5f5f5', 
      padding: '40px 20px',
      fontFamily: "'Rubik', sans-serif" 
    }}>
      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            color: '#470172',
            marginBottom: '8px',
            fontFamily: "'Rubik', sans-serif"
          }}>
            {isEdit ? 'Edit Policy/Scheme' : 'Add New Policy/Scheme'}
          </h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            {isEdit ? 'Update the details below' : 'Fill in the information to create a new entry'}
          </p>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Document Type"
            name="type"
            rules={[{ required: true, message: 'Please select document type' }]}
          >
            <Select 
              placeholder="Select document type" 
              size="large"
              style={{ fontFamily: "'Rubik', sans-serif" }}
            >
              <Option value="policy">Policy</Option>
              <Option value="scheme">Scheme</Option>
              <Option value="maharashtra-government-policy">Maharashtra Government Policy</Option>
              <Option value="central-government-policy">Central Government Policy</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Title"
            name="title"
            rules={[
              { required: true, message: 'Please enter title' },
              { max: 200, message: 'Title must be less than 200 characters' }
            ]}
          >
            <Input 
              placeholder="Enter policy/scheme title" 
              size="large"
              style={{ fontFamily: "'Rubik', sans-serif" }}
            />
          </Form.Item>

          <Form.Item
            label="Sector"
            name="sector"
          >
            <Select
              placeholder="Select sector"
              size="large"
              allowClear
              showSearch
              loading={industriesLoading}
              optionFilterProp="children"
              style={{ fontFamily: "'Rubik', sans-serif" }}
            >
              {industries.map((industry) => (
                <Option key={industry._id} value={industry.sectorName}>
                  {industry.sectorName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: 'Please enter description' },
              { max: 1000, message: 'Description must be less than 1000 characters' }
            ]}
          >
            <TextArea 
              placeholder="Provide a detailed description"
              rows={5}
              style={{ fontFamily: "'Rubik', sans-serif" }}
            />
          </Form.Item>

          <Form.Item
            label="Release Date"
            name="releaseDate"
            rules={[{ required: true, message: 'Please select release date' }]}
          >
            <Input
              type="date"
              size="large"
              style={{ fontFamily: "'Rubik', sans-serif" }}
            />
          </Form.Item>

          <Form.Item
            label="Scope"
            name="isInternational"
            rules={[{ required: true, message: 'Please select scope' }]}
          >
            <Radio.Group style={{ fontFamily: "'Rubik', sans-serif" }}>
              <Radio value={false}>National</Radio>
              <Radio value={true}>International</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="Upload Document"
            required={!isEdit}
            tooltip="Upload a PDF, DOC, DOCX, or TXT file (max 50MB)"
          >
            <Upload
              accept=".pdf,.doc,.docx,.txt"
              maxCount={1}
              beforeUpload={() => false}
              onChange={handleFileChange}
              showUploadList={false}
            >
              <Button 
                icon={<UploadOutlined />} 
                size="large"
                style={{ fontFamily: "'Rubik', sans-serif" }}
              >
                {file ? 'Change Document' : 'Upload Document'}
              </Button>
            </Upload>
            
            {filePreview && (
              <div style={{ 
                marginTop: 16, 
                padding: 16,
                border: '2px solid #470172', 
                borderRadius: 8, 
                backgroundColor: '#f9f0ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileTextOutlined style={{ fontSize: 24, color: '#470172', marginRight: 12 }} />
                  <div>
                    <div style={{ fontWeight: 600, color: '#333' }}>
                      {file ? file.name : filePreview}
                    </div>
                    {file && (
                      <div style={{ fontSize: 14, color: '#666' }}>
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    )}
                  </div>
                </div>
                <Button 
                  type="text" 
                  danger 
                  onClick={() => {
                    setFile(null);
                    setFilePreview(null);
                  }}
                >
                  Remove
                </Button>
              </div>
            )}
            
            {!filePreview && !isEdit && (
              <div style={{ 
                marginTop: 16, 
                padding: 40, 
                border: '2px dashed #d9d9d9', 
                borderRadius: 8,
                textAlign: 'center',
                color: '#999'
              }}>
                <FileTextOutlined style={{ fontSize: 48, marginBottom: 8 }} />
                <div>No document selected</div>
              </div>
            )}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button 
                onClick={() => navigate('/admin/policies')} 
                size="large"
                style={{ fontFamily: "'Rubik', sans-serif" }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={isLoading}
                size="large"
                style={{
                  fontFamily: "'Rubik', sans-serif",
                  backgroundColor: '#470172',
                  borderColor: '#470172'
                }}
              >
                {isEdit ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default PolicyForm;
